/*

This is a button whose appearance is primarily controlled from the outside
(e.g. using the data from the backend) by passing a styles object to it.

Since it is controlled by the backend, it will most likely be used only as a link

*/

import React, { PureComponent } from 'react'
import classNames from 'classnames'

import {
  hexToRGB,
  stringifyRGB,
  shadeRGBColor,
} from 'client/shared/helpers/color-helpers'

import Linka from 'client/shared/blocks/linka'
import { Loader } from 'client/shared/blocks/loader'

import './styled-button.styl'

type Props = {
  style: {
    [key: string]: string | number
  }
  loading?: boolean
  children: React.ReactNode | JSX.Element
  onClick?: () => void
  path?: string
}

type State = {
  hovering: boolean
}

class StyledButton extends PureComponent<Props, State> {
  state = {
    hovering: false,
  }

  handleMouseEnter = (): void => {
    if (!this.state.hovering) {
      this.setState({ hovering: true })
    }
  }

  handleMouseLeave = (): void => {
    if (this.state.hovering) {
      this.setState({ hovering: false })
    }
  }

  getBackgroundColor(): { [key: string]: string | number } {
    let {
      style: { backgroundColor },
    } = this.props

    if (typeof backgroundColor === 'string' && this.state.hovering) {
      const backgroundColorRGB = hexToRGB(backgroundColor)
      if (backgroundColorRGB) {
        backgroundColor = stringifyRGB(shadeRGBColor(backgroundColorRGB, -0.2))
      }
    }

    return { backgroundColor }
  }

  getStyles(): { [key: string]: any } {
    return {
      ...this.props.style,
      ...this.getBackgroundColor(),
    }
  }

  getCommonProps(): { [key: string]: any } {
    const { loading } = this.props
    const className = classNames('styled-button', {
      'styled-button_loading': loading,
    })
    return {
      className,
      style: this.getStyles(),
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      children: loading ? <Loader /> : this.props.children,
    }
  }

  render(): JSX.Element {
    if (this.props.onClick) {
      return this.renderButton(this.props.onClick)
    } else {
      return this.renderLink(this.props.path as string)
    }
  }

  renderLink(path: string): JSX.Element {
    return <Linka path={path} {...this.getCommonProps()} />
  }

  renderButton(onClick: () => void): JSX.Element {
    return <button {...this.getCommonProps()} onClick={onClick} />
  }
}

export default StyledButton
